import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBackOrigin } from "@/hooks/useBackTo";
import { Loading } from "@/components/Loading";
import { SortableTH } from "@/components/SortableTH";
import { Empty } from "@/components/Empty";
import { Dash } from "@/components/Dash";
import { useColumnSort } from "@/hooks/useColumnSort";
import { Pagination } from "@/components/Pagination";
import { StatusPill, USER_TIER_TONES, USER_STATUS_TONES } from "@/components/StatusPill";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/SearchInput";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePagedList } from "@/hooks/usePagedList";
import type { SortClause } from "@/lib/paging";
import { cn } from "@/lib/utils";
import { users, type UserListItem } from "@/features/users/api/users.api";
import { DeleteUserDialog } from "@/features/users/components/DeleteUser";
import { USER_ROLE_TIER_LABELS } from "@/features/auth/api/auth.api";
import { formatDateTime } from "@/utils/dates";

type SortKey = "userNumber" | "name" | "email" | "role" | "status" | "lastLoginAt";

const USERS_DEFAULT_SORT: SortClause[] = [
  { by: "role", dir: "asc" },
  { by: "status", dir: "asc" },
  { by: "email", dir: "asc" },
];

const COLUMN_MAP: Record<SortKey, string> = {
  userNumber: "userNumber",
  name: "name",
  email: "email",
  role: "role",
  status: "status",
  lastLoginAt: "lastLoginAt",
};

export default function UsersPage() {
  const navigate = useNavigate();
  const origin = useBackOrigin();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { data, loading, loaded, error, forbidden, keyword, sortBy, sortDir, pageNumber, setKeyword, setPage, setSort, reload } =
    usePagedList<UserListItem>(users.list, { defaultSort: USERS_DEFAULT_SORT, syncUrl: true });
  const rows = data.items;

  const { thProps } = useColumnSort<SortKey>(COLUMN_MAP, sortBy, sortDir, setSort);

  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Users</h1>
          <p className="text-sm text-slate-500 mt-1">
            All portal users. Click an user to edit; use New User to add
            a sub-user.
          </p>
        </div>
        <Button asChild className="self-start">
          <Link to="/manage/users/new" state={origin}>New user</Link>
        </Button>
      </header>

      {forbidden && (
        <Notice tone="warn">You don't have permission to view users (code 70.05).</Notice>
      )}
      {error && !forbidden && <Notice tone="error">{error}</Notice>}
      {!forbidden && !error && (
        <div className="mb-3 flex">
          <SearchInput
            value={keyword}
            onSearch={setKeyword}
            placeholder="Search by name, email, or username…"
          />
        </div>
      )}

      {!forbidden && !error && loading && !loaded && <Loading>Loading users…</Loading>}

      {!forbidden && !error && loaded && (
        <div className={cn("rounded-md border bg-white shadow-sm transition-opacity", loading && loaded && "opacity-60")}>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTH {...thProps("userNumber")}>#</SortableTH>
                <SortableTH {...thProps("name")}>Name</SortableTH>
                <SortableTH {...thProps("email")}>Email</SortableTH>
                <SortableTH {...thProps("role")}>Tier</SortableTH>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Role</TableHead>
                <SortableTH {...thProps("status")}>Status</SortableTH>
                <SortableTH {...thProps("lastLoginAt")}>Last login</SortableTH>
                <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow
                  key={a.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/manage/users/${a.id}`, { state: origin })}
                >
                  <TableCell className="text-slate-500 tabular-nums">{a.userNumber ?? <Dash />}</TableCell>
                  <TableCell className="font-medium">
                    <Link to={`/manage/users/${a.id}`} state={origin} className="text-slate-900 hover:underline">
                      {a.name}
                    </Link>
                    {a.managerName && (
                      <div className="text-xs text-slate-500">Reports to {a.managerName}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {a.email}
                    {a.username && <div className="text-xs text-slate-500">@{a.username}</div>}
                  </TableCell>
                  <TableCell><StatusPill value={a.role} map={USER_TIER_TONES} labels={USER_ROLE_TIER_LABELS} /></TableCell>
                  <TableCell className="text-slate-700">{a.assignedRoleName ?? <Dash />}</TableCell>
                  <TableCell><StatusPill value={a.status} map={USER_STATUS_TONES} /></TableCell>
                  <TableCell className="text-slate-700 tabular-nums whitespace-nowrap">
                    {a.lastLoginAt ? formatDateTime(a.lastLoginAt) : <Empty>Never</Empty>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-destructive"
                      aria-label={`Delete ${a.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteId(a.id);
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination page={data} onPageChange={setPage} busy={loading} />
        </div>
      )}

      <DeleteUserDialog
        userId={deleteId}
        onClose={() => setDeleteId(null)}
        onDeleted={() => {
          setDeleteId(null);
          if (rows.length === 1 && pageNumber > 1) setPage(pageNumber - 1);
          else reload();
        }}
      />
    </>
  );
}

